//================================================================
//  Component: Submit Quote For Review Pane
//================================================================

//  Purpose: This side pane allows the user to submit a quote for review

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the sidePane}
//    - setPaneOpen = { useState, updates the boolean value that opens and closes the sidePane}
//    - task = { useState, store the related parent Commercial task}
//    - currentQuoteReviewTask = { useState, used to store if there is a current quote review task}

//  Example:
//  
//    <SubmitQuoteForReviewPane
//      paneOpen={paneOpen}
//      setPaneOpen={setPaneOpen}
//      task={task}
//      currentQuoteReviewTask={currentQuoteReviewTask}
//    ></SubmitQuoteForReviewPane>

//================================================================

//Libraries
import { useContext, useState, useEffect, useReducer } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../../../Library/WriteDocument';
import DocumentListener from '../../../../Library/DocumentListener';
import UploadFile from '../../../../Library/UploadFile';

//Images
import Loading from '../../../../Components/Images/Image_Loading_Ripple.svg';
import FileIcon from '../../../../Components/Images/Icon_File_Black.svg';

//CSS


export default function SubmitQuoteForReviewPane({
    paneOpen,
    setPaneOpen,
    task,
    currentQuoteReviewTask
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, handleSubmit, formState: { errors }, watch, reset, setValue, clearErrors } = useForm();

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to set pane status > 'onload', 'pending', 'error'
    const [paneStatus, setPaneStatus] = useState('pending');

    // Used to determine the request owner
    const [requestOwner, setRequestOwner] = useState(task?.createdby);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store attached files
    const [documentData, setDocumentData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'documents': [],
            'documentsErrorMessage': '',
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submit Handler 
    const onSubmit = (value) => {

        setPaneStatus('pending');

        // Upload Files - Ensure all file uploads are completed before proceeding
        const filePromises = documentData.documents.map((document) =>
            UploadFile(`requests/${currentQuoteReviewTask?.requestid}/${document?.fileId}`, document?.fileObject)
        );

        return Promise.all(filePromises)
        .then((results) => {

            const attachedFiles = [];

            // Loop through docs > Add URL
            documentData?.documents.forEach((document, index) => {

                attachedFiles.push({
                    'fileUrl': results[index],
                    'fileId': document?.fileId,
                    'fileName': document?.fileName,
                    'fileType': document?.fileType
                })

            });

            // Prepare Updates to Quote Task
            const quoteTask = {
                'status': 'Initialised',
                'activityfeedvisible': true,
                'taskdescription': value.comment,
                'assignedto': requestOwner,
                'assigneddate': new Date(),
                'lastmodifieddate': new Date(),
                'lastmodifiedby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'comment': value.comment,
                'attachedfiles': attachedFiles
            };

            return WriteDocument('requests', currentQuoteReviewTask?.requestid, quoteTask, true)
            .then(() => {

                // Reset form and UI state
                reset();
                clearErrors();
                setDocumentData({ documents: [], documentsErrorMessage: '' });
                setPaneStatus('onload');
                setPaneOpen(false);

            })

        })

        .catch((error) => {
            console.error('error', error);
            setPaneStatus('error');

        })

    }

    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------

    // Onload
    // - Get assigned digital business partner if required
    useEffect(() => {

        if (task === undefined) return;

        function onLoadChange(document){

            // Set request owner to assigned business partner if the request origin is the Demand Management Portal
            if (task?.requestorigin === 'Demand') {
                setRequestOwner(document?.assignedto)
            }

            setPaneStatus('onload');

        }
    
        function onError(error){
            console.log('error', error);
            setPaneStatus('error');

        }
        
        const unsubscribe = DocumentListener('requests', task?.parentrequestid, onLoadChange, onLoadChange, onError);
        
        return () =>{
            unsubscribe();
        };

    // eslint-disable-next-line 
    }, [task])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    // ==================================================
    //  Hide Side Pane
    // ==================================================

    if (!paneOpen) return null;

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    if (paneStatus === 'onload') {

        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container'>

                    {/* ------------------------------- */}
                    {/*  Form                           */}
                    {/* ------------------------------- */}

                    <form className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> Submit Quote for Review </h4>
                        </div>

                        {/* Body */}
                        <div className='w-[50vw] h-full flex flex-col gap-3 overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/* ===================================== */}
                            {/*  Request Owner                        */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> Request Owner </label>

                                <div className='flex flex-wrap p-2 gap-1 border-solid border-1 border-[#D8D8D8] rounded-[5px] overflow-x-hidden'>

                                    <p className='m-0 py-1 px-3 bg-[#DDF3F5] text-[#1C96A3] text-[15px] rounded-lg capitalize'>
                                        {requestOwner?.givenname} {requestOwner?.surname}
                                    </p>

                                </div>
                            </div>

                            {/* ===================================== */}
                            {/*  Comment                              */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium mt-0'>Comment <span className='text-[#C4314B]'>*</span> </label>
                                <textarea
                                    className={errors.comment ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                                    type='text'
                                    placeholder='Enter a comment'
                                    {...register('comment', {
                                        required: 'This field is required'
                                    })}
                                ></textarea>

                                {errors.comment && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.comment?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Attach Documents                     */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium mb-1'> Attach Document(s) </label>

                                {/* File Upload */}
                                <div className='Attach-File-Onload-Container'>

                                    <input
                                        className={documentData?.documentsErrorMessage?.length > 0 ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                        style={{ padding: '0px' }}
                                        type='file'
                                        accept='.pdf,.doc,.txt,.xlsx,.docx,.csv,.png,.jpg'
                                        onChange={(evt) => {

                                            const fileObject = evt.target.files[0];

                                            // Validate File Types
                                            const allowedFileTypes = ['pdf', 'doc', 'txt', 'xlsx', 'docx', 'csv', 'png', 'jpg'];

                                            let fileType;
                                            fileType = fileObject?.name.split('.');
                                            fileType = fileType[fileType?.length - 1].toLowerCase();

                                            if (allowedFileTypes.includes(fileType) === false) {
                                                return setDocumentData({ 'documentsErrorMessage': `Please upload a file in the following format(s): ${allowedFileTypes.join(', ')}.` });

                                            }

                                            // Validate File Size < 20 MB
                                            if (fileObject.size > 20000000) {
                                                return setDocumentData({ 'documentsErrorMessage': 'File size too large. Please upload a file smaller than 20MB.' });

                                            }

                                            // Update Form Data
                                            const fileId = `${Date.now().toString()}${Math.floor(Math.random() * (99999 - 10000 + 1) + 10000)}.${fileType}`;

                                            setDocumentData({
                                                'documents': [
                                                    ...documentData.documents,
                                                    {
                                                        'fileUrl': '',
                                                        'fileId': fileId,
                                                        'fileName': fileObject.name,
                                                        'fileObject': fileObject,
                                                        'fileType': fileType
                                                    }
                                                ],
                                                'documentsErrorMessage': ''
                                            });

                                            // Clear File Input
                                            evt.target.value = '';

                                        }}
                                        disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled') ? true : false}
                                    ></input>

                                </div>

                                {/* Error Message */}
                                <label className='font-medium text-[#DE0000]' hidden={documentData?.documentsErrorMessage.length === 0}>{documentData?.documentsErrorMessage}</label>

                                {/* Uploads Table */}
                                {
                                    documentData?.documents.length > 0 &&
                                    <div className='w-full flex flex-col text-[#0972D3] rounded-md underline border border-[#D8D8D8] mt-1'>
                                        {
                                            documentData?.documents.map((doc, index) => (
                                                <div key={index} className='flex flex-row justify-between items-center border-b border-b-[#D8D8D8] last:border-0 px-3 py-[2px]'>

                                                    {/* File Name */}
                                                    <div className='flex flex-row gap-2 items-center'>
                                                        <img src={FileIcon} alt='file'></img>
                                                        <p className='mb-0'>{doc?.fileName}</p>
                                                    </div>

                                                    {/* Delete Button */}
                                                    <div className='Cancel-Icon min-w-[30px] mt-[5px] mb-0 p-0' onClick={() => setDocumentData({ 'documents': documentData.documents.filter(item => item !== doc) })}></div>

                                                </div>
                                            ))
                                        }
                                    </div>
                                }
                            </div>

                        </div>

                        {/* ------------------------------- */}
                        {/*  Submit Buttons                 */}
                        {/* ------------------------------- */}

                        <div className='flex flex-row gap-2 px-4 py-3'>

                            {/* Submit */}
                            <button className='Primary-Button' type='submit' value='submit' disabled={watch('comment')?.length === 0 || errors.comment || documentData?.documentsErrorMessage.length > 0 ? true : false}>
                                Submit
                            </button>

                            {/* Cancel */}
                            <button
                                className='Secondary-Button'
                                onClick={() => {

                                    // Reset Form and Close Side Pane
                                    setPaneOpen(false);
                                    reset();
                                    setValue('quantity', 0);
                                    setValue('unitprice', 0);

                                    // Check to see if this is the first quote line item and set the currency 
                                    if (currentQuoteReviewTask?.quote?.length > 0) {
                                        setValue('currency', task?.agreement?.currency)
                                    }

                                    clearErrors();

                                }}
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </dialog>
            </div>
        );
    }

    // ---------------------------------------------------
    //  Pending
    // ---------------------------------------------------

    else if (paneStatus === 'pending') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container flex justify-center items-center text-center'>
                    <img alt='loading-circle-icon' src={Loading}></img>
                </dialog>
            </div>
        )
    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else if (paneStatus === 'error') {
        return (
            <div className='Pane-Background'>

                {/* Pane Container */}
                <dialog className='Pane-Container'>
                    <div className='flex flex-col gap-2 w-full justify-center items-center'>

                        <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
                        <h4 className='mb-0'>Oops! Something went wrong.</h4>
                        <p className='text-center leading-[1.7]'>
                            An error occurred while we processed your request.
                        </p>
                        <button
                            className='Primary-Button'
                            onClick={() => {

                                // Reset Form and Close Pane
                                setPaneOpen(false);
                                reset();
                                clearErrors();

                            }}
                        >
                            Close
                        </button>

                    </div>
                </dialog>
            </div>

        )
    }

    //------------------------------------------------------

}